import { useState, useEffect, useRef, useCallback } from "react";
import { fsGet, fsSet, fsGetUsers, fsSaveUsers } from "./firebase";

// ── 預設資料 ──────────────────────────────────────────────
const DEFAULT_USERS = [
  {
    id: 1,
    name: "Cyndi",
    password: "admin888",
    role: "admin",
    projects: ["ALL"],
  },
];
const COMPANIES = ["鈊誠科技", "誠立方資訊", "藝誠國際", "天誠趨勢", "其他"];
const EV_CATS = [
  "發文排程",
  "文案優化",
  "AEO/GEO",
  "客戶溝通",
  "數據分析",
  "素材製作",
  "網站內容",
  "其他",
];
const KPI_OPTS = [
  "貼文觸及率成長達標",
  "官網導流點擊達標",
  "AEO/GEO 關鍵字曝光提升",
  "客戶續約",
  "方案升級",
  "其他指標",
];
const PLATFORMS_DEFAULT = [
  { id: 1, name: "Facebook", color: "#1877f2" },
  { id: 2, name: "Instagram", color: "#e1306c" },
  { id: 3, name: "Google 我的商家", color: "#34a853" },
  { id: 4, name: "YouTube", color: "#ff0000" },
  { id: 5, name: "LinkedIn", color: "#0a66c2" },
  { id: 6, name: "TikTok", color: "#69c9d0" },
  { id: 7, name: "Pinterest", color: "#e60023" },
  { id: 8, name: "X (Twitter)", color: "#1da1f2" },
  { id: 9, name: "Threads", color: "#111" },
  { id: 10, name: "LINE 官方帳號", color: "#06c755" },
].map((p) => ({ ...p, link: "", note: "" }));

// ── 工具函式 ──────────────────────────────────────────────
function pad(n) {
  return String(n).padStart(2, "0");
}
function toDay() {
  return new Date().toISOString().slice(0, 10);
}
function toMonth() {
  const d = new Date();
  return d.getFullYear() + "-" + pad(d.getMonth() + 1);
}
function uid() {
  return Date.now() + Math.random().toString(36).slice(2, 6);
}
function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}
function useIsMobile() {
  const [m, setM] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return m;
}

// ── 操作記錄（audit log）──────────────────────────────────
let CURRENT_USER = null;
export function setCurrentUser(u) {
  CURRENT_USER = u;
}
const KEY_NAMES = {
  contracts: "合約",
  platforms: "行銷平台",
  brand: "品牌設定",
  events: "AI專案記錄",
  performances: "數據績效",
  tasks: "執行項目",
  bonuses: "獎金發放",
  splits: "公司拆帳",
  "splits-hist": "拆帳歷史",
  projects: "專案設定",
  users: "人員帳號",
};
let _logCache = null;
export function logChangeGlobal(key) {
  logChange("_global", key);
}
async function logChange(proj, key) {
  if (!CURRENT_USER) return;
  try {
    if (_logCache === null) {
      _logCache = (await fsGet("_global", "auditlog", [])) || [];
    }
    const now = Date.now();
    const last = _logCache[_logCache.length - 1];
    // 同一人同一模組 60 秒內合併為一筆
    if (
      last &&
      last.user === CURRENT_USER.name &&
      last.proj === proj &&
      last.key === key &&
      now - last.t < 60000
    ) {
      last.t = now;
    } else {
      _logCache.push({ t: now, user: CURRENT_USER.name, proj, key });
      if (_logCache.length > 100) _logCache = _logCache.slice(-100);
    }
    fsSet("_global", "auditlog", _logCache);
  } catch (e) {}
}

// ── Firebase store hook ───────────────────────────────────
function useFStore(proj, key, def) {
  const lsKey = "ic_" + proj + "_" + key;
  const [val, setVal] = useState(() => {
    try {
      const r = localStorage.getItem(lsKey);
      return r ? JSON.parse(r) : def;
    } catch (e) {
      return def;
    }
  });
  useEffect(() => {
    fsGet(proj, key, null).then((remote) => {
      if (remote !== null) {
        localStorage.setItem(lsKey, JSON.stringify(remote));
        setVal(remote);
      }
    });
  }, [proj, key]); // eslint-disable-line
  const set = useCallback(
    (v) => {
      setVal((prev) => {
        const nv = typeof v === "function" ? v(prev) : v;
        localStorage.setItem(lsKey, JSON.stringify(nv));
        fsSet(proj, key, nv);
        logChange(proj, key);
        return nv;
      });
    },
    [proj, key, lsKey]
  );
  return [val, set];
}
function useUsers() {
  const [users, setU] = useState(null);
  useEffect(() => {
    fsGetUsers().then((u) => setU(u || DEFAULT_USERS));
  }, []);
  function save(u) {
    fsSaveUsers(u);
    logChangeGlobal("users");
    setU([...u]);
  }
  return [users, save];
}

// ── 色盤 ──────────────────────────────────────────────────
const C = {
  bg: "#0f172a",
  bg2: "#1e293b",
  bg3: "#263148",
  accent: "#38bdf8",
  accent2: "#0ea5e9",
  adim: "rgba(56,189,248,.12)",
  green: "#34d399",
  amber: "#fbbf24",
  rose: "#fb7185",
  purple: "#a78bfa",
  text: "#f1f5f9",
  text2: "#94a3b8",
  text3: "#64748b",
  border: "rgba(148,163,184,.12)",
  border2: "rgba(148,163,184,.2)",
};
const card = {
  background: C.bg2,
  border: "1px solid " + C.border,
  borderRadius: 12,
  padding: "16px 18px",
  marginBottom: 14,
};
const bA = {
  background: C.accent2,
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "8px 14px",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap",
};
const bG = {
  background: "transparent",
  border: "1px solid " + C.border2,
  color: C.text2,
  borderRadius: 8,
  padding: "8px 14px",
  fontSize: 12,
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap",
};
const bEdit = {
  background: "rgba(56,189,248,.1)",
  border: "1px solid rgba(56,189,248,.25)",
  color: C.accent,
  borderRadius: 7,
  padding: "4px 12px",
  fontSize: 12,
  cursor: "pointer",
  fontFamily: "inherit",
};
const bDel = {
  background: "rgba(251,113,133,.08)",
  border: "1px solid rgba(251,113,133,.25)",
  color: C.rose,
  borderRadius: 7,
  padding: "4px 12px",
  fontSize: 12,
  cursor: "pointer",
  fontFamily: "inherit",
};
const bSm = {
  background: "transparent",
  border: "1px solid " + C.border2,
  color: C.text3,
  borderRadius: 7,
  padding: "3px 10px",
  fontSize: 11,
  cursor: "pointer",
  fontFamily: "inherit",
};
const fi = {
  background: C.bg3,
  border: "1px solid " + C.border2,
  borderRadius: 8,
  padding: "10px 12px",
  color: C.text,
  fontFamily: "inherit",
  fontSize: 14,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};
const fl = {
  fontSize: 12,
  color: C.text3,
  fontWeight: 500,
  letterSpacing: ".3px",
  marginBottom: 4,
  display: "block",
};

// ── 共用元件 ──────────────────────────────────────────────
function Fld({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={fl}>{label}</span>
      {children}
    </div>
  );
}
function CTtl({ children }) {
  return (
    <div
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: C.accent,
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      {children}
    </div>
  );
}
function Tag({ bg, color, children }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 500,
        background: bg,
        color,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}
function Empty({ icon, text }) {
  return (
    <div style={{ textAlign: "center", padding: "36px 20px", color: C.text3 }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: 14 }}>{text}</div>
    </div>
  );
}
function StatCard({ val, label, color, bc }) {
  return (
    <div
      style={{
        ...card,
        borderColor: bc || C.border,
        marginBottom: 0,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          fontFamily: "monospace",
          color: color || C.text,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {val}
      </div>
      <div style={{ fontSize: 11, color: C.text3, marginTop: 4 }}>{label}</div>
    </div>
  );
}
function IBar({ type, children }) {
  const s =
    type === "amber"
      ? { bg: "rgba(251,191,36,.08)", b: "rgba(251,191,36,.2)", c: C.amber }
      : { bg: "rgba(56,189,248,.08)", b: "rgba(56,189,248,.2)", c: C.accent };
  return (
    <div
      style={{
        background: s.bg,
        border: "1px solid " + s.b,
        color: s.c,
        borderRadius: 8,
        padding: "8px 14px",
        fontSize: 12,
        marginBottom: 14,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}
function CBox({ checked, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        width: 20,
        height: 20,
        borderRadius: 4,
        border: "1px solid " + (checked ? C.green : C.border2),
        background: checked ? C.green : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        color: "#fff",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      {checked ? "✓" : ""}
    </div>
  );
}
function RoleBadge({ role }) {
  const m = {
    admin: {
      t: "管理員",
      bg: "rgba(52,211,153,.12)",
      c: "#34d399",
      b: "rgba(52,211,153,.3)",
    },
    partner: {
      t: "專案夥伴",
      bg: "rgba(167,139,250,.12)",
      c: "#a78bfa",
      b: "rgba(167,139,250,.3)",
    },
    client: {
      t: "客戶",
      bg: "rgba(251,191,36,.12)",
      c: "#fbbf24",
      b: "rgba(251,191,36,.3)",
    },
  };
  const s = m[role] || m.client;
  return (
    <div
      style={{
        padding: "4px 12px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 500,
        background: s.bg,
        color: s.c,
        border: "1px solid " + s.b,
        whiteSpace: "nowrap",
      }}
    >
      {s.t}
    </div>
  );
}
function Loader() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: C.bg,
        flexDirection: "column",
        gap: 16,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          border: "3px solid " + C.border2,
          borderTop: "3px solid " + C.accent,
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <div style={{ color: C.text3, fontSize: 13 }}>載入中…</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function DTable({ cols, rows }) {
  return (
    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <table
        style={{ width: "100%", borderCollapse: "collapse", minWidth: 360 }}
      >
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c}
                style={{
                  fontSize: 11,
                  color: C.text3,
                  padding: "8px 10px",
                  borderBottom: "1px solid " + C.border,
                  textAlign: "left",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}
function TR({ cells }) {
  return (
    <tr style={{ borderBottom: "1px solid " + C.border }}>
      {cells.map((c, i) => (
        <td
          key={i}
          style={{
            padding: "10px 10px",
            fontSize: 13,
            color: C.text2,
            verticalAlign: "middle",
          }}
        >
          {c ?? ""}
        </td>
      ))}
    </tr>
  );
}

function Modal({ title, onClose, onSave, children, saveLabel }) {
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.7)",
        zIndex: 600,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: C.bg2,
          border: "1px solid " + C.border2,
          borderRadius: "16px 16px 0 0",
          padding: "24px 20px",
          width: "100%",
          maxWidth: 560,
          maxHeight: "88vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            background: C.border2,
            borderRadius: 2,
            margin: "0 auto 20px",
          }}
        />
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>
          {title}
        </div>
        {children}
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 22,
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <button onClick={onClose} style={{ ...bG, flex: 1 }}>
            取消
          </button>
          <button onClick={onSave} style={{ ...bA, flex: 2 }}>
            {saveLabel || "確認儲存"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── 登入畫面 ──────────────────────────────────────────────
// ══════════════════════════════════════════════════════════
function LoginScreen({ onLogin, users }) {
  const [pass, setPass] = useState(""),
    [err, setErr] = useState("");
  if (!users) return <Loader />;
  function submit() {
    const u = users.find((x) => x.password === pass);
    if (!u) {
      setErr("密碼錯誤，請確認後再試");
      return;
    }
    setErr("");
    onLogin(u);
  }
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: C.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          background: C.bg2,
          border: "1px solid " + C.border2,
          borderRadius: 20,
          padding: "36px 28px",
          width: "100%",
          maxWidth: 380,
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: C.accent,
              letterSpacing: ".5px",
            }}
          >
            鈊誠數位
          </div>
          <div
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: C.accent,
              letterSpacing: "1px",
              marginTop: 8,
            }}
          >
            AI 專案行銷管理系統
          </div>
          <div style={{ fontSize: 11, color: C.text3, marginTop: 6 }}>
            Intelligent Marketing Project Platform
          </div>
          <div style={{ height: 1, background: C.border, margin: "18px 0" }} />
        </div>
        <Fld label="請輸入您的專屬密碼">
          <input
            style={{ ...fi, fontSize: 16 }}
            type="password"
            placeholder="輸入密碼後自動識別身份"
            value={pass}
            autoFocus
            onChange={(e) => setPass(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </Fld>
        <div
          style={{
            color: C.rose,
            fontSize: 13,
            textAlign: "center",
            minHeight: 18,
          }}
        >
          {err}
        </div>
        <button
          onClick={submit}
          style={{
            ...bA,
            padding: 14,
            fontSize: 15,
            width: "100%",
            borderRadius: 10,
          }}
        >
          進入系統
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── 側欄設定 ──────────────────────────────────────────────
// ══════════════════════════════════════════════════════════
const NAV = [
  { id: "client-info", icon: "📋", label: "客戶基本資料", sec: "專案資料" },
  { id: "ai-project", icon: "📅", label: "AI 專案管理", sec: "執行管理" },
  { id: "performance", icon: "📊", label: "數據績效", sec: "" },
  { id: "tasks", icon: "⚙️", label: "執行項目", sec: "", noClient: true },
  {
    id: "bonus",
    icon: "💰",
    label: "獎金發放",
    sec: "財務管理",
    noClient: true,
  },
  {
    id: "split",
    icon: "🏢",
    label: "公司拆帳",
    sec: "",
    noClient: true,
    adminOnly: true,
  },
  {
    id: "user-mgmt",
    icon: "🔐",
    label: "人員帳號管理",
    sec: "系統設定",
    adminOnly: true,
  },
];

// ══════════════════════════════════════════════════════════
// ── 主程式 ────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════
export default function App() {
  const [users, saveUsers] = useUsers();
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("client-info");
  const [curProj, setCurProj] = useState("A001");
  const [allProj, setAllProj] = useState([{ id: "A001", name: "客戶 A001" }]);
  const [toast, setToast] = useState("");
  const [sideOpen, setSideOpen] = useState(false);
  const isMobile = useIsMobile();
  const tmr = useRef(null);

  useEffect(() => {
    fsGet("_global", "projects", [{ id: "A001", name: "客戶 A001" }]).then(
      (p) => {
        if (p && p.length) {
          const normalized = p.map((x) =>
            typeof x === "string" ? { id: x, name: x } : x
          );
          setAllProj(normalized);
        }
      }
    );
  }, []);

  // 權限守衛：確保 curProj 永遠在使用者授權範圍內（解決 race condition）
  useEffect(() => {
    if (!user || !allProj.length) return;
    const isAdm =
      user.role === "admin" || (user.projects || []).includes("ALL");
    const okIds = isAdm
      ? allProj.map((p) => p.id)
      : (user.projects || []).filter((id) => allProj.some((p) => p.id === id));
    if (!okIds.includes(curProj) && okIds.length) {
      setCurProj(okIds[0]);
    }
  }, [user, allProj, curProj]);

  function goPage(id) {
    setPage(id);
    if (isMobile) setSideOpen(false);
  }
  function showToast(msg) {
    setToast(msg);
    clearTimeout(tmr.current);
    tmr.current = setTimeout(() => setToast(""), 2400);
  }
  const isAdmin = user?.role === "admin",
    isClient = user?.role === "client";
  const proj = curProj;
  const projName = allProj.find((p) => p.id === curProj)?.name || curProj;

  if (!users) return <Loader />;
  if (!user)
    return (
      <LoginScreen
        onLogin={(u) => {
          setUser(u);
          setCurrentUser(u);
          setPage("client-info");
        }}
        users={users}
      />
    );

  // 計算此用戶可看到的專案清單
  const visibleProj =
    !user || isAdmin
      ? allProj
      : (user.projects || []).includes("ALL")
      ? allProj
      : allProj.filter((p) => (user.projects || []).includes(p.id));

  // 無授權專案防護：非管理員且沒有任何可存取專案時，顯示提示畫面
  if (!isAdmin && visibleProj.length === 0) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: C.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 16,
          padding: 20,
        }}
      >
        <div style={{ fontSize: 40 }}>🔒</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: C.text }}>
          尚未取得專案存取權限
        </div>
        <div style={{ fontSize: 13, color: C.text3, textAlign: "center" }}>
          請聯絡管理員為您的帳號指派專案後再登入。
        </div>
        <button
          onClick={() => setUser(null)}
          style={{ ...bA, padding: "10px 24px" }}
        >
          返回登入
        </button>
      </div>
    );
  }

  const sp = { user, proj, showToast };

  return (
    <div
      style={{
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: C.bg,
        color: C.text,
        fontFamily: "'Noto Sans TC',sans-serif",
        fontSize: 14,
        overflow: "hidden",
      }}
    >
      {/* TOPBAR */}
      <div
        style={{
          minHeight: 54,
          background: C.bg2,
          borderBottom: "1px solid " + C.border,
          display: "flex",
          alignItems: "center",
          padding: "0 14px",
          paddingTop: "env(safe-area-inset-top)",
          gap: 10,
          flexShrink: 0,
          zIndex: 400,
        }}
      >
        <button
          onClick={() => setSideOpen((o) => !o)}
          style={{
            background: "none",
            border: "none",
            color: sideOpen ? C.accent : C.text2,
            cursor: "pointer",
            padding: 6,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 20,
              height: 2,
              background: "currentColor",
              borderRadius: 1,
            }}
          />
          <div
            style={{
              width: 20,
              height: 2,
              background: "currentColor",
              borderRadius: 1,
            }}
          />
          <div
            style={{
              width: 20,
              height: 2,
              background: "currentColor",
              borderRadius: 1,
            }}
          />
        </button>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: C.accent,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {projName}
          </div>
          <div
            style={{
              fontSize: 10,
              color: C.text3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            鈊誠數位・AI 專案行銷管理系統
          </div>
        </div>
        {visibleProj.length > 1 && (
          <select
            value={curProj}
            onChange={(e) => setCurProj(e.target.value)}
            style={{
              ...fi,
              width: "auto",
              fontSize: 12,
              padding: "4px 8px",
              flexShrink: 0,
            }}
          >
            {visibleProj.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || p.id}
              </option>
            ))}
          </select>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: C.adim,
              border: "1px solid " + C.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              color: C.accent,
              fontWeight: 600,
            }}
          >
            {user.name[0]}
          </div>
          {!isMobile && (
            <span
              style={{ fontSize: 12, color: C.text2, whiteSpace: "nowrap" }}
            >
              {user.name}
            </span>
          )}
          {!isMobile && <RoleBadge role={user.role} />}
          <button
            onClick={() => {
              setUser(null);
              setCurrentUser(null);
            }}
            style={{ ...bG, fontSize: 11, padding: "5px 10px" }}
          >
            登出
          </button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {isMobile && sideOpen && (
          <div
            onClick={() => setSideOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              top: "calc(54px + env(safe-area-inset-top))",
              background: "rgba(0,0,0,.5)",
              zIndex: 200,
              WebkitTapHighlightColor: "transparent",
            }}
          />
        )}

        {/* SIDEBAR */}
        <div
          style={{
            position: isMobile ? "fixed" : "relative",
            left: isMobile ? (sideOpen ? 0 : -260) : 0,
            top: isMobile ? "calc(54px + env(safe-area-inset-top))" : 0,
            bottom: 0,
            width: 240,
            background: C.bg2,
            borderRight: "1px solid " + C.border,
            display: "flex",
            flexDirection: "column",
            padding: "10px 0",
            zIndex: 300,
            overflowY: "auto",
            transition: "left .25s ease",
            flexShrink: 0,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {isMobile && (
            <div
              style={{
                padding: "12px 16px 16px",
                borderBottom: "1px solid " + C.border,
                marginBottom: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: C.adim,
                    border: "1px solid " + C.accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    color: C.accent,
                    fontWeight: 700,
                  }}
                >
                  {user.name[0]}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {user.name}
                  </div>
                  <RoleBadge role={user.role} />
                </div>
              </div>
            </div>
          )}
          {NAV.map((item, idx) => {
            if (item.adminOnly && !isAdmin) return null;
            if (item.noClient && isClient) return null;
            const showSec =
              item.sec && (idx === 0 || NAV[idx - 1]?.sec !== item.sec);
            return (
              <div key={item.id}>
                {showSec && (
                  <div
                    style={{
                      padding: "10px 16px 4px",
                      fontSize: 10,
                      color: C.text3,
                      letterSpacing: ".8px",
                      textTransform: "uppercase",
                      fontWeight: 500,
                    }}
                  >
                    {item.sec}
                  </div>
                )}
                <div
                  onClick={() => goPage(item.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "11px 16px",
                    cursor: "pointer",
                    borderLeft:
                      "3px solid " +
                      (page === item.id ? C.accent : "transparent"),
                    background: page === item.id ? C.adim : "transparent",
                    color: page === item.id ? C.accent : C.text2,
                    fontSize: 14,
                    userSelect: "none",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                  }}
                >
                  <span
                    style={{
                      fontSize: 16,
                      width: 20,
                      textAlign: "center",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </span>
                  {item.label}
                </div>
              </div>
            );
          })}
          {isMobile && (
            <div
              style={{
                marginTop: "auto",
                padding: 16,
                borderTop: "1px solid " + C.border,
              }}
            >
              <button
                onClick={() => {
                  setUser(null);
                  setCurrentUser(null);
                  setSideOpen(false);
                }}
                style={{
                  ...bG,
                  width: "100%",
                  justifyContent: "center",
                  padding: 10,
                }}
              >
                登出
              </button>
            </div>
          )}
        </div>

        {/* CONTENT */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 16,
            background: C.bg,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {page === "client-info" && <PageClientInfo key={proj} {...sp} />}
          {page === "ai-project" && <PageCalendar key={proj} {...sp} />}
          {page === "performance" && <PagePerformance key={proj} {...sp} />}
          {page === "tasks" && !isClient && <PageTasks key={proj} {...sp} />}
          {page === "bonus" && !isClient && <PageBonus key={proj} {...sp} />}
          {page === "split" && isAdmin && <PageSplit key={proj} {...sp} />}
          {page === "user-mgmt" && isAdmin && (
            <PageUserMgmt
              showToast={showToast}
              users={users}
              saveUsers={saveUsers}
              allProj={allProj}
              setAllProj={setAllProj}
            />
          )}
        </div>
      </div>
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "calc(20px + env(safe-area-inset-bottom))",
            left: "50%",
            transform: "translateX(-50%)",
            background: C.bg3,
            border: "1px solid " + C.green,
            borderRadius: 20,
            padding: "10px 20px",
            color: C.green,
            fontSize: 13,
            zIndex: 9999,
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── 🔐 人員帳號管理 ───────────────────────────────────────
// ══════════════════════════════════════════════════════════
function PageUserMgmt({ showToast, users, saveUsers, allProj, setAllProj }) {
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [editingProj, setEditingProj] = useState(null);
  const [projEditForm, setProjEditForm] = useState({});
  const [auditLog, setAuditLog] = useState([]);
  function loadLog() {
    fsGet("_global", "auditlog", []).then((l) => setAuditLog(l || []));
  }
  useEffect(() => {
    loadLog();
  }, []);
  const f = (k) => form[k] ?? "";
  const sf = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  function saveProj(p) {
    fsSet("_global", "projects", p);
    logChangeGlobal("projects");
    setAllProj([...p]);
  }
  function openAdd() {
    setEditing(null);
    setForm({ role: "partner", projects: [] });
    setModal("user");
  }
  function openEdit(u) {
    setEditing(u);
    setForm({ ...u, projects: u.projects || [] });
    setModal("user");
  }
  function saveUser() {
    if (!f("name") || !f("password")) {
      showToast("姓名和密碼為必填");
      return;
    }
    const dup = users.find(
      (u) => u.password === f("password") && (!editing || u.id !== editing.id)
    );
    if (dup) {
      showToast("此密碼已被其他人員使用");
      return;
    }
    if (editing) {
      saveUsers(
        users.map((u) => (u.id === editing.id ? { ...u, ...form } : u))
      );
      showToast("帳號已更新");
    } else {
      saveUsers([...users, { id: uid(), ...form }]);
      showToast("帳號已新增");
    }
    setModal(null);
  }
  function delUser(id) {
    if (
      users.filter((u) => u.role === "admin").length <= 1 &&
      users.find((u) => u.id === id)?.role === "admin"
    ) {
      showToast("至少保留一位管理員");
      return;
    }
    saveUsers(users.filter((u) => u.id !== id));
    showToast("帳號已刪除");
  }
  function addProj() {
    const id = f("np")?.trim(),
      name = f("nn")?.trim();
    if (!id) {
      showToast("請輸入專案編號");
      return;
    }
    if (allProj.find((x) => x.id === id)) {
      showToast("此編號已存在");
      return;
    }
    saveProj([...allProj, { id, name: name || id }]);
    sf("np", "");
    sf("nn", "");
    showToast("已新增");
  }
  function toggleP(p) {
    const cur = form.projects || [];
    sf("projects", cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);
  }

  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
        人員帳號管理
      </div>
      <div style={{ fontSize: 13, color: C.text3, marginBottom: 20 }}>
        新增、編輯系統人員帳號與專案存取權限
      </div>

      {/* ── 專案管理（含名稱編輯）── */}
      <div style={card}>
        <CTtl>📁 專案管理</CTtl>
        {allProj.map((p) => (
          <div
            key={p.id}
            style={{
              background: C.bg3,
              borderRadius: 8,
              padding: "12px 14px",
              marginBottom: 10,
              border: "1px solid " + C.border,
            }}
          >
            {editingProj === p.id ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                  }}
                >
                  <Fld label="專案編號（不可修改）">
                    <input
                      style={{ ...fi, opacity: 0.6 }}
                      value={p.id}
                      disabled
                    />
                  </Fld>
                  <Fld label="客戶名稱（顯示用）">
                    <input
                      style={fi}
                      value={projEditForm.name || ""}
                      placeholder="例：王小明咖啡廳"
                      onChange={(e) =>
                        setProjEditForm((x) => ({ ...x, name: e.target.value }))
                      }
                    />
                  </Fld>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                  }}
                >
                  <button onClick={() => setEditingProj(null)} style={bG}>
                    取消
                  </button>
                  <button
                    onClick={() => {
                      saveProj(
                        allProj.map((x) =>
                          x.id === p.id
                            ? { ...x, name: projEditForm.name || p.id }
                            : x
                        )
                      );
                      setEditingProj(null);
                      showToast("已更新");
                    }}
                    style={bA}
                  >
                    儲存
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {p.name || p.id}
                  </div>
                  <div style={{ fontSize: 12, color: C.text3 }}>
                    編號：{p.id}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setEditingProj(p.id);
                    setProjEditForm({ name: p.name || "" });
                  }}
                  style={bEdit}
                >
                  編輯名稱
                </button>
                <button
                  onClick={() => {
                    saveProj(allProj.filter((x) => x.id !== p.id));
                    showToast("已刪除");
                  }}
                  style={bDel}
                >
                  刪除
                </button>
              </div>
            )}
          </div>
        ))}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 4,
          }}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
          >
            <Fld label="新專案編號">
              <input
                style={fi}
                placeholder="例：B002"
                value={f("np")}
                onChange={(e) => sf("np", e.target.value)}
              />
            </Fld>
            <Fld label="客戶名稱">
              <input
                style={fi}
                placeholder="例：王小明咖啡廳"
                value={f("nn")}
                onChange={(e) => sf("nn", e.target.value)}
              />
            </Fld>
          </div>
          <button onClick={addProj} style={{ ...bA, alignSelf: "flex-start" }}>
            ＋ 新增專案
          </button>
        </div>
      </div>

      {/* ── 人員帳號 ── */}
      <div style={card}>
        <CTtl>
          👤 人員帳號列表
          <button
            onClick={openAdd}
            style={{ ...bA, marginLeft: "auto", fontSize: 12 }}
          >
            ＋ 新增人員
          </button>
        </CTtl>
        {!users.length ? (
          <Empty icon="👤" text="尚未新增任何人員" />
        ) : (
          users.map((u) => (
            <div
              key={u.id}
              style={{
                background: C.bg3,
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 10,
                border: "1px solid " + C.border,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: C.adim,
                    border: "1px solid " + C.accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    color: C.accent,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {u.name[0]}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{u.name}</div>
                  <RoleBadge role={u.role} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => openEdit(u)} style={bEdit}>
                    編輯
                  </button>
                  <button onClick={() => delUser(u.id)} style={bDel}>
                    刪除
                  </button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: C.text3 }}>
                可存取：
                {(u.projects || []).includes("ALL")
                  ? "全部專案"
                  : (u.projects || []).join("、") || "—"}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── 操作記錄 ── */}
      <div style={card}>
        <CTtl>
          📋 操作記錄
          <span style={{ fontSize: 12, color: C.text3, fontWeight: 400 }}>
            最近 {auditLog.length} 筆
          </span>
          <button
            onClick={loadLog}
            style={{ ...bG, marginLeft: "auto", fontSize: 12 }}
          >
            🔄 重新整理
          </button>
        </CTtl>
        {!auditLog.length ? (
          <Empty icon="📋" text="尚無操作記錄" />
        ) : (
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {[...auditLog].reverse().map((log, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid " + C.border,
                  fontSize: 12,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    color: C.text3,
                    fontFamily: "monospace",
                    flexShrink: 0,
                  }}
                >
                  {fmtTime(new Date(log.t).toISOString())}
                </span>
                <strong style={{ color: C.accent }}>{log.user}</strong>
                <span style={{ color: C.text3 }}>修改了</span>
                {log.proj !== "_global" && (
                  <Tag bg={C.adim} color={C.accent}>
                    {allProj.find((p) => p.id === log.proj)?.name || log.proj}
                  </Tag>
                )}
                <Tag bg="rgba(251,191,36,.1)" color={C.amber}>
                  {KEY_NAMES[log.key] || log.key}
                </Tag>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal === "user" && (
        <Modal
          title={editing ? "編輯帳號" : "新增人員帳號"}
          onClose={() => setModal(null)}
          onSave={saveUser}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Fld label="姓名">
              <input
                style={fi}
                value={f("name")}
                placeholder="請輸入姓名"
                onChange={(e) => sf("name", e.target.value)}
              />
            </Fld>
            <Fld label="專屬登入密碼">
              <input
                style={fi}
                value={f("password")}
                placeholder="設定唯一密碼"
                onChange={(e) => sf("password", e.target.value)}
              />
            </Fld>
            <Fld label="身份權限">
              <select
                style={fi}
                value={f("role")}
                onChange={(e) => sf("role", e.target.value)}
              >
                <option value="admin">管理員（全功能）</option>
                <option value="partner">專案夥伴（執行管理）</option>
                <option value="client">客戶（唯讀檢視）</option>
              </select>
            </Fld>
            <Fld label="可存取的專案">
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  marginTop: 4,
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={(form.projects || []).includes("ALL")}
                    onChange={() =>
                      sf(
                        "projects",
                        (form.projects || []).includes("ALL") ? [] : ["ALL"]
                      )
                    }
                  />
                  <span style={{ color: C.accent, fontWeight: 500 }}>
                    全部專案
                  </span>
                </label>
                {!(form.projects || []).includes("ALL") &&
                  allProj.map((p) => (
                    <label
                      key={p.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={(form.projects || []).includes(p.id)}
                        onChange={() => toggleP(p.id)}
                      />
                      <span>{p.name || p.id}</span>
                    </label>
                  ))}
              </div>
            </Fld>
            <div
              style={{
                fontSize: 12,
                color: C.text3,
                background: C.bg3,
                borderRadius: 8,
                padding: "10px 12px",
              }}
            >
              💡 每人密碼必須唯一，登入時系統自動識別身份。
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── ① 客戶基本資料 ────────────────────────────────────────
// ══════════════════════════════════════════════════════════
function PageClientInfo({ user, proj, showToast }) {
  const isAdmin = user.role === "admin",
    isClient = user.role === "client";
  const [contracts, setContracts] = useFStore(proj, "contracts", []);
  const [platforms, setPlatforms] = useFStore(
    proj,
    "platforms",
    PLATFORMS_DEFAULT
  );
  const [brand, setBrandRaw] = useFStore(proj, "brand", {
    style: "",
    notes: "",
    preference: "",
    updatedAt: "",
    updatedBy: "",
  });
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [platManage, setPlatManage] = useState(false);
  const f = (k) => form[k] ?? "";
  const sf = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  function setBrand(updater) {
    setBrandRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return {
        ...next,
        updatedAt: new Date().toISOString(),
        updatedBy: user.name,
      };
    });
  }
  function openC(c) {
    setEditing(c || null);
    setForm(c || { payStatus: "pending" });
    setModal("c");
  }
  function saveC() {
    if (editing) {
      setContracts((l) =>
        l.map((x) => (x.id === editing.id ? { ...x, ...form } : x))
      );
      showToast("合約已更新");
    } else {
      setContracts((l) => [...l, { id: uid(), payStatus: "pending", ...form }]);
      showToast("合約已新增");
    }
    setModal(null);
  }
  function openP(p) {
    setEditing(p || null);
    setForm(p || { color: "#38bdf8" });
    setModal("p");
  }
  function saveP() {
    if (editing) {
      setPlatforms((l) =>
        l.map((x) => (x.id === editing.id ? { ...x, ...form } : x))
      );
      showToast("平台已更新");
    } else {
      setPlatforms((l) => [...l, { id: uid(), link: "", note: "", ...form }]);
      showToast("平台已新增");
    }
    setModal(null);
  }

  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
        客戶基本資料
      </div>
      <div style={{ fontSize: 13, color: C.text3, marginBottom: 18 }}>
        管理客戶合約、行銷平台及品牌設定
      </div>
      {isClient && <IBar type="amber">👁 客戶檢視模式，僅能瀏覽資料。</IBar>}

      {/* 合約 */}
      <div style={card}>
        <CTtl>
          📄 合約項目管理
          {!isClient && (
            <button
              onClick={() => openC(null)}
              style={{ ...bA, marginLeft: "auto", fontSize: 12 }}
            >
              ＋ 新增合約
            </button>
          )}
        </CTtl>
        {!contracts.length ? (
          <Empty icon="📄" text="尚未新增合約" />
        ) : (
          contracts.map((c) => (
            <div
              key={c.id}
              style={{
                background: C.bg3,
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 10,
                border: "1px solid " + C.border,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                  flexWrap: "wrap",
                }}
              >
                <strong style={{ fontSize: 14 }}>{c.name}</strong>
                <Tag bg={C.adim} color={C.accent}>
                  {c.item}
                </Tag>
                {!isClient && (
                  <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    <button onClick={() => openC(c)} style={bEdit}>
                      編輯
                    </button>
                    <button
                      onClick={() => {
                        setContracts((l) => l.filter((x) => x.id !== c.id));
                        showToast("已刪除");
                      }}
                      style={bDel}
                    >
                      刪除
                    </button>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 13, marginBottom: 4 }}>
                <span style={{ color: C.text3, fontSize: 11 }}>合約期間　</span>
                {c.start} ～ {c.end}
              </div>
              <div style={{ fontSize: 13, marginBottom: 10 }}>
                <span style={{ color: C.text3, fontSize: 11 }}>款項　</span>
                <span style={{ color: C.green, fontWeight: 600 }}>
                  NT$ {c.amount}
                </span>
              </div>
              {c.note && (
                <div style={{ fontSize: 12, color: C.text3, marginBottom: 10 }}>
                  📝 {c.note}
                </div>
              )}
              {/* ── 收款進度 ── */}
              <div
                style={{ paddingTop: 10, borderTop: "1px solid " + C.border }}
              >
                <div style={{ fontSize: 11, color: C.text3, marginBottom: 8 }}>
                  收款進度
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: isClient ? "default" : "pointer",
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="radio"
                      name={"pay-" + c.id}
                      checked={c.payStatus === "received"}
                      disabled={isClient}
                      onChange={() => {
                        if (!isClient)
                          setContracts((l) =>
                            l.map((x) =>
                              x.id === c.id
                                ? { ...x, payStatus: "received" }
                                : x
                            )
                          );
                      }}
                    />
                    <span style={{ color: C.green, fontWeight: 500 }}>
                      ✓ 已收款
                    </span>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: isClient ? "default" : "pointer",
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="radio"
                      name={"pay-" + c.id}
                      checked={c.payStatus === "pending" || !c.payStatus}
                      disabled={isClient}
                      onChange={() => {
                        if (!isClient)
                          setContracts((l) =>
                            l.map((x) =>
                              x.id === c.id ? { ...x, payStatus: "pending" } : x
                            )
                          );
                      }}
                    />
                    <span style={{ color: C.amber, fontWeight: 500 }}>
                      ◷ 待收款
                    </span>
                  </label>
                  {!isClient ? (
                    <input
                      type="date"
                      style={{
                        ...fi,
                        width: "auto",
                        fontSize: 12,
                        padding: "5px 8px",
                      }}
                      value={c.payDate || ""}
                      onChange={(e) =>
                        setContracts((l) =>
                          l.map((x) =>
                            x.id === c.id
                              ? { ...x, payDate: e.target.value }
                              : x
                          )
                        )
                      }
                    />
                  ) : (
                    c.payDate && (
                      <span style={{ fontSize: 12, color: C.text3 }}>
                        {c.payDate}
                      </span>
                    )
                  )}
                  {c.payStatus === "received" && (
                    <Tag bg="rgba(52,211,153,.1)" color={C.green}>
                      已收款
                    </Tag>
                  )}
                  {(!c.payStatus || c.payStatus === "pending") && (
                    <Tag bg="rgba(251,191,36,.1)" color={C.amber}>
                      待收款
                    </Tag>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 平台 */}
      <div style={card}>
        <CTtl>
          🌐 行銷平台設定
          <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
            {!isClient && !platManage && (
              <button
                onClick={() => setPlatManage(true)}
                style={{ ...bG, fontSize: 12 }}
              >
                ✎ 管理
              </button>
            )}
            {!isClient && platManage && (
              <button
                onClick={() => setPlatManage(false)}
                style={{ ...bA, fontSize: 12 }}
              >
                ✓ 完成
              </button>
            )}
            {!isClient && platManage && (
              <button
                onClick={() => openP(null)}
                style={{ ...bG, fontSize: 12 }}
              >
                ＋ 新增
              </button>
            )}
          </div>
        </CTtl>
        {platManage && !isClient && (
          <div
            style={{
              fontSize: 12,
              color: C.text3,
              marginBottom: 10,
              padding: "6px 10px",
              background: C.bg3,
              borderRadius: 8,
            }}
          >
            ☰ 長按拖拉可排序，點 ✕ 刪除平台
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {platforms.map((p, idx) => (
            <div
              key={p.id}
              draggable={platManage && !isClient}
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", String(idx));
                e.currentTarget.style.opacity = "0.4";
              }}
              onDragEnd={(e) => {
                e.currentTarget.style.opacity = "1";
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.style.background = C.bg3;
              }}
              onDragLeave={(e) => {
                e.currentTarget.style.background = "";
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.style.background = "";
                const from = parseInt(e.dataTransfer.getData("text/plain"));
                if (from === idx) return;
                const arr = [...platforms];
                const [moved] = arr.splice(from, 1);
                arr.splice(idx, 0, moved);
                setPlatforms(arr);
                showToast("排序已更新");
              }}
              style={{
                background: C.bg3,
                border: "1px solid " + C.border,
                borderRadius: 8,
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: platManage ? "grab" : "default",
                transition: "background .15s",
              }}
            >
              {platManage && !isClient && (
                <div
                  style={{
                    fontSize: 16,
                    color: C.text3,
                    flexShrink: 0,
                    cursor: "grab",
                    userSelect: "none",
                  }}
                >
                  ☰
                </div>
              )}
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: p.color,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontWeight: 500,
                  fontSize: 13,
                  minWidth: 80,
                  flexShrink: 0,
                }}
              >
                {p.name}
              </span>
              <input
                style={{ ...fi, fontSize: 12, padding: "6px 9px", flex: 1 }}
                placeholder="貼上平台連結"
                value={p.link || ""}
                disabled={isClient}
                onChange={(e) =>
                  setPlatforms((l) =>
                    l.map((x) =>
                      x.id === p.id ? { ...x, link: e.target.value } : x
                    )
                  )
                }
              />
              {!platManage && p.link && (
                <a
                  href={p.link}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontSize: 11,
                    color: C.accent,
                    flexShrink: 0,
                    textDecoration: "none",
                  }}
                >
                  開啟
                </a>
              )}
              {platManage && !isClient && (
                <button
                  onClick={() => {
                    setPlatforms((l) => l.filter((x) => x.id !== p.id));
                    showToast("已刪除");
                  }}
                  style={{ ...bDel, padding: "3px 8px", flexShrink: 0 }}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 品牌設定 */}
      <div style={card}>
        <CTtl>🎨 行銷內容設定</CTtl>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Fld label="品牌視覺風格">
            <textarea
              style={{ ...fi, minHeight: 80, resize: "vertical" }}
              disabled={isClient}
              placeholder="例：清新自然、日系風格…"
              value={brand.style || ""}
              onChange={(e) =>
                setBrand((b) => ({ ...b, style: e.target.value }))
              }
            />
          </Fld>
          <Fld label="發文注意事項">
            <textarea
              style={{ ...fi, minHeight: 80, resize: "vertical" }}
              disabled={isClient}
              placeholder="例：不提競品、避免政治話題…"
              value={brand.notes || ""}
              onChange={(e) =>
                setBrand((b) => ({ ...b, notes: e.target.value }))
              }
            />
          </Fld>
          <Fld label="客戶個人傾向">
            <textarea
              style={{ ...fi, minHeight: 72, resize: "vertical" }}
              disabled={isClient}
              placeholder="例：個性急躁、喜歡偏保守風格、不喜歡太多文字…"
              value={brand.preference || ""}
              onChange={(e) =>
                setBrand((b) => ({ ...b, preference: e.target.value }))
              }
            />
          </Fld>
        </div>
        {brand.updatedAt && (
          <div style={{ marginTop: 10, fontSize: 11, color: C.text3 }}>
            ✏️ 最後修改：{fmtTime(brand.updatedAt)}　由{" "}
            <strong style={{ color: C.text2 }}>{brand.updatedBy}</strong>
          </div>
        )}
      </div>

      {modal === "c" && (
        <Modal
          title={editing ? "編輯合約" : "新增合約"}
          onClose={() => setModal(null)}
          onSave={saveC}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <Fld label="客戶名稱">
              <input
                style={fi}
                value={f("name")}
                placeholder="客戶公司名稱"
                onChange={(e) => sf("name", e.target.value)}
              />
            </Fld>
            <Fld label="合約項目">
              <input
                style={fi}
                value={f("item")}
                placeholder="例：AI 行銷月訂閱"
                onChange={(e) => sf("item", e.target.value)}
              />
            </Fld>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <Fld label="起始日">
                <input
                  style={fi}
                  type="date"
                  value={f("start")}
                  onChange={(e) => sf("start", e.target.value)}
                />
              </Fld>
              <Fld label="到期日">
                <input
                  style={fi}
                  type="date"
                  value={f("end")}
                  onChange={(e) => sf("end", e.target.value)}
                />
              </Fld>
            </div>
            <Fld label="合約款項（NT$）">
              <input
                style={fi}
                value={f("amount")}
                placeholder="例：8,000"
                onChange={(e) => sf("amount", e.target.value)}
              />
            </Fld>
            <Fld label="其他附註">
              <textarea
                style={{ ...fi, minHeight: 60 }}
                value={f("note")}
                placeholder="備註…"
                onChange={(e) => sf("note", e.target.value)}
              />
            </Fld>
            <Fld label="收款進度">
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginTop: 4,
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  <input
                    type="radio"
                    checked={f("payStatus") === "received"}
                    onChange={() => sf("payStatus", "received")}
                  />
                  <span style={{ color: C.green, fontWeight: 500 }}>
                    已收款
                  </span>
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  <input
                    type="radio"
                    checked={f("payStatus") === "pending" || !f("payStatus")}
                    onChange={() => sf("payStatus", "pending")}
                  />
                  <span style={{ color: C.amber, fontWeight: 500 }}>
                    待收款
                  </span>
                </label>
                <input
                  type="date"
                  style={{
                    ...fi,
                    width: "auto",
                    fontSize: 13,
                    padding: "6px 10px",
                  }}
                  value={f("payDate")}
                  onChange={(e) => sf("payDate", e.target.value)}
                />
              </div>
            </Fld>
          </div>
        </Modal>
      )}
      {modal === "p" && (
        <Modal
          title={editing ? "編輯平台" : "新增平台"}
          onClose={() => setModal(null)}
          onSave={saveP}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <Fld label="平台名稱">
              <input
                style={fi}
                value={f("name")}
                placeholder="例：YouTube、Dcard…"
                onChange={(e) => sf("name", e.target.value)}
              />
            </Fld>
            <Fld label="代表顏色">
              <input
                style={{ ...fi, height: 44, cursor: "pointer" }}
                type="color"
                value={f("color") || "#38bdf8"}
                onChange={(e) => sf("color", e.target.value)}
              />
            </Fld>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── ② AI 專案管理 ─────────────────────────────────────────
// ══════════════════════════════════════════════════════════
function PageCalendar({ user, proj, showToast }) {
  const isClient = user.role === "client";
  const isMobile = useIsMobile();
  const [yr, setYr] = useState(new Date().getFullYear());
  const [mo, setMo] = useState(new Date().getMonth());
  const [events, setEvents] = useFStore(proj, "events", []);
  const [filterPerson, setFilterPerson] = useState("全部");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const f = (k) => form[k] ?? "";
  const sf = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  function mvMo(d) {
    let m = mo + d,
      y = yr;
    if (m > 11) {
      m = 0;
      y++;
    }
    if (m < 0) {
      m = 11;
      y--;
    }
    setMo(m);
    setYr(y);
  }
  const first = new Date(yr, mo, 1).getDay(),
    days = new Date(yr, mo + 1, 0).getDate(),
    prev = new Date(yr, mo, 0).getDate();
  const tod = new Date();
  const EVC = ["#38bdf8", "#34d399", "#fbbf24", "#a78bfa"];
  const people = [...new Set(events.map((e) => e.person).filter(Boolean))];
  const moStr = yr + "-" + pad(mo + 1);
  const moEvents = events.filter((e) => e.date?.startsWith(moStr));
  const catCount = {};
  moEvents.forEach((e) => {
    catCount[e.cat || "其他"] = (catCount[e.cat || "其他"] || 0) + 1;
  });
  function openAdd(date) {
    setForm({
      date: date || toDay(),
      person: user.name,
      cat: "發文排程",
      title: "",
      detail: "",
    });
    setModal(true);
  }
  function openEdit(e) {
    if (isClient) return;
    setForm({ ...e });
    setModal(true);
  }
  function save() {
    if (!f("title")) {
      showToast("請輸入執行標題");
      return;
    }
    if (f("id")) {
      setEvents((l) => l.map((e) => (e.id === form.id ? { ...form } : e)));
      showToast("已更新");
    } else {
      setEvents((l) => [...l, { ...form, id: uid() }]);
      showToast("記錄已新增");
    }
    setModal(false);
  }
  function del(id) {
    setEvents((l) => l.filter((e) => e.id !== id));
    setModal(false);
    showToast("已刪除");
  }
  const cellH = isMobile ? 56 : 70;
  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
        AI 專案管理
      </div>
      <div style={{ fontSize: 13, color: C.text3, marginBottom: 16 }}>
        月曆記錄每日執行內容，可依人員篩選查看
      </div>
      {isClient && <IBar type="amber">👁 客戶檢視模式，僅能瀏覽資料。</IBar>}
      {moEvents.length > 0 && (
        <div style={{ ...card, marginBottom: 14 }}>
          <CTtl>
            📊 {yr}/{mo + 1} 月摘要
          </CTtl>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <StatCard
              val={moEvents.length}
              label="總記錄筆數"
              color={C.accent}
              bc="rgba(56,189,248,.3)"
            />
            <StatCard
              val={
                [...new Set(moEvents.map((e) => e.person).filter(Boolean))]
                  .length
              }
              label="參與人員"
              color={C.green}
              bc="rgba(52,211,153,.3)"
            />
            <StatCard
              val={[...new Set(moEvents.map((e) => e.date))].length}
              label="有記錄天數"
              color={C.purple}
              bc="rgba(167,139,250,.3)"
            />
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Object.entries(catCount)
              .sort((a, b) => b[1] - a[1])
              .map(([cat, cnt]) => (
                <div
                  key={cat}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    background: C.bg3,
                    borderRadius: 20,
                    padding: "4px 10px",
                    fontSize: 12,
                  }}
                >
                  <span style={{ color: C.text2 }}>{cat}</span>
                  <span style={{ color: C.accent, fontWeight: 600 }}>
                    {cnt}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
      <div style={card}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            {yr} 年 {mo + 1} 月
          </div>
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {people.length > 0 && (
              <select
                value={filterPerson}
                onChange={(e) => setFilterPerson(e.target.value)}
                style={{
                  ...fi,
                  width: "auto",
                  fontSize: 12,
                  padding: "6px 8px",
                }}
              >
                <option value="全部">全部</option>
                {people.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            )}
            <button
              onClick={() => mvMo(-1)}
              style={{ ...bG, padding: "6px 12px" }}
            >
              ◀
            </button>
            <button
              onClick={() => mvMo(1)}
              style={{ ...bG, padding: "6px 12px" }}
            >
              ▶
            </button>
            {!isClient && (
              <button onClick={() => openAdd("")} style={bA}>
                ＋ 新增
              </button>
            )}
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7,1fr)",
            gap: 2,
          }}
        >
          {["日", "一", "二", "三", "四", "五", "六"].map((d) => (
            <div
              key={d}
              style={{
                textAlign: "center",
                fontSize: 10,
                color: C.text3,
                padding: "4px 0",
                fontWeight: 600,
              }}
            >
              {d}
            </div>
          ))}
          {Array.from({ length: first }, (_, i) => (
            <div
              key={"p" + i}
              style={{
                minHeight: cellH,
                background: C.bg2,
                border: "1px solid " + C.border,
                borderRadius: 6,
                padding: 4,
              }}
            >
              <div style={{ fontSize: 10, color: C.text3 }}>
                {prev - first + 1 + i}
              </div>
            </div>
          ))}
          {Array.from({ length: days }, (_, i) => {
            const d = i + 1,
              ds = yr + "-" + pad(mo + 1) + "-" + pad(d);
            const isT =
              tod.getFullYear() === yr &&
              tod.getMonth() === mo &&
              tod.getDate() === d;
            const de = (
              filterPerson === "全部"
                ? events
                : events.filter((e) => e.person === filterPerson)
            ).filter((e) => e.date === ds);
            return (
              <div
                key={d}
                onClick={() => !isClient && openAdd(ds)}
                style={{
                  minHeight: cellH,
                  background: C.bg2,
                  border: "1px solid " + (isT ? C.accent : C.border),
                  borderRadius: 6,
                  padding: 4,
                  cursor: isClient ? "default" : "pointer",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    marginBottom: 2,
                    color: isT ? C.accent : C.text,
                  }}
                >
                  {d}
                </div>
                {de.slice(0, isMobile ? 2 : 3).map((e, idx) => (
                  <div
                    key={e.id}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      openEdit(e);
                    }}
                    style={{
                      fontSize: 9,
                      borderRadius: 2,
                      padding: "1px 3px",
                      marginBottom: 1,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      background: EVC[idx % 4] + "22",
                      color: EVC[idx % 4],
                      cursor: isClient ? "default" : "pointer",
                    }}
                  >
                    {e.person ? e.person[0] + "·" : ""}
                    {e.title}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      {people.length > 0 && (
        <div style={card}>
          <CTtl>
            👥 人員工作記錄{" "}
            <span style={{ fontSize: 12, color: C.text3, fontWeight: 400 }}>
              （{mo + 1} 月）
            </span>
          </CTtl>
          {(filterPerson === "全部" ? people : [filterPerson]).map((person) => {
            const pe = moEvents.filter((e) => e.person === person);
            if (!pe.length) return null;
            return (
              <div key={person} style={{ marginBottom: 16 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: C.adim,
                      border: "1px solid " + C.accent,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      color: C.accent,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {person[0]}
                  </div>
                  <strong style={{ fontSize: 14 }}>{person}</strong>
                  <span style={{ fontSize: 12, color: C.text3 }}>
                    {pe.length} 筆
                  </span>
                </div>
                {pe
                  .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
                  .map((e) => (
                    <div
                      key={e.id}
                      style={{
                        background: C.bg3,
                        borderRadius: 8,
                        padding: "10px 12px",
                        marginBottom: 8,
                        border: "1px solid " + C.border,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          marginBottom: 4,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: C.text3,
                            flexShrink: 0,
                          }}
                        >
                          {e.date}
                        </span>
                        <Tag bg={C.adim} color={C.accent}>
                          {e.cat || "—"}
                        </Tag>
                        <strong style={{ fontSize: 13 }}>{e.title}</strong>
                      </div>
                      {e.detail && (
                        <div style={{ fontSize: 12, color: C.text3 }}>
                          {e.detail}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      )}
      {modal && (
        <Modal
          title={f("id") ? "編輯執行記錄" : "新增執行記錄"}
          onClose={() => setModal(false)}
          onSave={save}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <Fld label="日期">
              <input
                style={fi}
                type="date"
                value={f("date")}
                onChange={(e) => sf("date", e.target.value)}
              />
            </Fld>
            <Fld label="執行人員">
              <input
                style={fi}
                value={f("person")}
                onChange={(e) => sf("person", e.target.value)}
              />
            </Fld>
            <Fld label="執行標題">
              <input
                style={fi}
                value={f("title")}
                placeholder="例：排定本月全平台貼文排程"
                onChange={(e) => sf("title", e.target.value)}
              />
            </Fld>
            <Fld label="詳細說明">
              <textarea
                style={{ ...fi, minHeight: 80 }}
                value={f("detail")}
                placeholder="說明執行內容…"
                onChange={(e) => sf("detail", e.target.value)}
              />
            </Fld>
            <Fld label="分類">
              <select
                style={fi}
                value={f("cat")}
                onChange={(e) => sf("cat", e.target.value)}
              >
                {EV_CATS.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </Fld>
            {f("id") && (
              <button
                onClick={() => del(form.id)}
                style={{ ...bDel, alignSelf: "flex-start", marginTop: 4 }}
              >
                刪除此記錄
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── ③ 數據績效 ────────────────────────────────────────────
// ══════════════════════════════════════════════════════════
function PagePerformance({ user, proj, showToast }) {
  const isAdmin = user.role === "admin",
    isClient = user.role === "client",
    isPartner = user.role === "partner";
  const [perfs, setPerfs] = useFStore(proj, "performances", []);
  const [filterMonth, setFilterMonth] = useState(toMonth());
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const f = (k) => form[k] ?? "";
  const sf = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const months = [
    ...new Set(perfs.map((p) => p.date?.slice(0, 7)).filter(Boolean)),
  ]
    .sort()
    .reverse();
  // 夥伴只能看自己的績效紀錄（避免看到他人獎金）
  const filtered = perfs.filter(
    (p) =>
      (!filterMonth || p.date?.startsWith(filterMonth)) &&
      (!isPartner || p.person === user.name)
  );
  const total = filtered.reduce((s, p) => s + (parseInt(p.bonus) || 0), 0);
  const rate = filtered.length
    ? Math.round(
        (filtered.filter((p) => p.bonus).length / filtered.length) * 100
      ) + "%"
    : "—";
  function openAdd() {
    setEditing(null);
    setForm({ date: toDay(), person: user.name, kpi: KPI_OPTS[0] });
    setModal(true);
  }
  function openEdit(p) {
    setEditing(p);
    setForm({ ...p });
    setModal(true);
  }
  function save() {
    if (editing) {
      setPerfs((l) =>
        l.map((x) => (x.id === editing.id ? { ...x, ...form } : x))
      );
      showToast("已更新");
    } else {
      setPerfs((l) => [...l, { id: uid(), ...form }]);
      showToast("績效紀錄已新增");
    }
    setModal(false);
  }
  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
        數據績效
      </div>
      <div style={{ fontSize: 13, color: C.text3, marginBottom: 16 }}>
        記錄執行成效，自動連動獎金計算
      </div>
      {isClient && <IBar type="amber">👁 客戶檢視模式，僅能瀏覽資料。</IBar>}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {!isClient && (
          <StatCard val={"$" + total.toLocaleString()} label="績效獎金" />
        )}
        <StatCard val={filtered.length} label="達標項目" />
        <StatCard val={rate} label="達標率" />
      </div>
      <div style={card}>
        <CTtl>
          📈 績效紀錄
          <div
            style={{
              display: "flex",
              gap: 8,
              marginLeft: "auto",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              style={{ ...fi, width: "auto", fontSize: 12, padding: "6px 8px" }}
            >
              <option value="">全部</option>
              {months.map((m) => (
                <option key={m}>{m}</option>
              ))}
              {!months.includes(toMonth()) && (
                <option value={toMonth()}>{toMonth()}</option>
              )}
            </select>
            {!isClient && (
              <button onClick={openAdd} style={bA}>
                ＋ 新增
              </button>
            )}
          </div>
        </CTtl>
        {!filtered.length ? (
          <Empty icon="📊" text="此月份尚無績效紀錄" />
        ) : (
          filtered.map((p) => (
            <div
              key={p.id}
              style={{
                background: C.bg3,
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 10,
                border: "1px solid " + C.border,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                  marginBottom: 6,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      flexWrap: "wrap",
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 12, color: C.text3 }}>
                      {p.date}
                    </span>
                    <Tag bg="rgba(52,211,153,.1)" color={C.green}>
                      {p.kpi}
                    </Tag>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    {p.person || "—"}
                  </div>
                  {p.detail && (
                    <div style={{ fontSize: 12, color: C.text3, marginTop: 4 }}>
                      {p.detail}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  {p.bonus && !isClient && (
                    <div
                      style={{
                        color: C.green,
                        fontFamily: "monospace",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      NT$ {p.bonus}
                    </div>
                  )}
                  {!isClient && (
                    <div
                      style={{
                        display: "flex",
                        gap: 5,
                        marginTop: 6,
                        justifyContent: "flex-end",
                      }}
                    >
                      <button onClick={() => openEdit(p)} style={bEdit}>
                        編輯
                      </button>
                      <button
                        onClick={() => {
                          setPerfs((l) => l.filter((x) => x.id !== p.id));
                          showToast("已刪除");
                        }}
                        style={bDel}
                      >
                        刪除
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {modal && (
        <Modal
          title={editing ? "編輯績效紀錄" : "新增績效紀錄"}
          onClose={() => setModal(false)}
          onSave={save}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <Fld label="日期">
              <input
                style={fi}
                type="date"
                value={f("date")}
                onChange={(e) => sf("date", e.target.value)}
              />
            </Fld>
            <Fld label="執行人員">
              <input
                style={fi}
                value={f("person")}
                onChange={(e) => sf("person", e.target.value)}
              />
            </Fld>
            <Fld label="達成指標">
              <select
                style={fi}
                value={f("kpi")}
                onChange={(e) => sf("kpi", e.target.value)}
              >
                {KPI_OPTS.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </Fld>
            <Fld label="具體說明">
              <textarea
                style={{ ...fi, minHeight: 80 }}
                value={f("detail")}
                placeholder="說明具體達成的數據或成效…"
                onChange={(e) => sf("detail", e.target.value)}
              />
            </Fld>
            {isAdmin ? (
              <Fld label="核發獎金（NT$）">
                <input
                  style={fi}
                  type="number"
                  value={f("bonus")}
                  placeholder="例：1000"
                  onChange={(e) => sf("bonus", e.target.value)}
                />
              </Fld>
            ) : (
              <div style={{ fontSize: 12, color: C.text3 }}>
                💡 獎金由管理員核發
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── ④ 執行項目 ────────────────────────────────────────────
// ══════════════════════════════════════════════════════════
function PageTasks({ user, proj, showToast }) {
  const isPartner = user.role === "partner";
  const [tasks, setTasks] = useFStore(proj, "tasks", []);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [showArchive, setShowArchive] = useState(false);
  const f = (k) => form[k] ?? "";
  const sf = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  // 夥伴只能看自己的項目（避免看到他人費用）
  const visible = tasks.filter((t) => !isPartner || t.person === user.name);
  // 自動封存：狀態為「完成」的項目歸入封存區
  const active = visible.filter((t) => t.status !== "完成");
  const archived = visible.filter((t) => t.status === "完成");
  function openAdd() {
    setEditing(null);
    setForm({ person: user.name, status: "進行中" });
    setModal(true);
  }
  function openEdit(t) {
    setEditing(t);
    setForm({ ...t });
    setModal(true);
  }
  function save() {
    if (editing) {
      setTasks((l) =>
        l.map((x) => (x.id === editing.id ? { ...x, ...form } : x))
      );
      showToast("已更新");
    } else {
      setTasks((l) => [...l, { id: uid(), ...form }]);
      showToast("執行項目已新增");
    }
    setModal(false);
  }
  function markDone(t) {
    setTasks((l) =>
      l.map((x) =>
        x.id === t.id
          ? { ...x, status: "完成", doneDate: x.doneDate || toDay() }
          : x
      )
    );
    showToast("已完成並封存");
  }
  function restore(t) {
    setTasks((l) =>
      l.map((x) => (x.id === t.id ? { ...x, status: "進行中" } : x))
    );
    showToast("已還原為進行中");
  }
  function TaskCard({ t, inArchive }) {
    return (
      <div
        style={{
          background: C.bg3,
          borderRadius: 8,
          padding: "12px 14px",
          marginBottom: 10,
          border: "1px solid " + (inArchive ? "rgba(52,211,153,.2)" : C.border),
          opacity: inArchive ? 0.85 : 1,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
              {t.name}
            </div>
            {t.detail && (
              <div style={{ fontSize: 12, color: C.text3, marginBottom: 4 }}>
                {t.detail}
              </div>
            )}
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 12, color: C.text2 }}>
                {t.person || "—"}
              </span>
              {t.fee && (
                <span
                  style={{
                    color: C.amber,
                    fontFamily: "monospace",
                    fontSize: 12,
                  }}
                >
                  NT$ {t.fee}
                </span>
              )}
              <Tag
                bg={
                  t.status === "完成"
                    ? "rgba(52,211,153,.1)"
                    : "rgba(251,191,36,.1)"
                }
                color={t.status === "完成" ? C.green : C.amber}
              >
                {t.status || "進行中"}
              </Tag>
              {t.doneDate && (
                <span style={{ fontSize: 11, color: C.text3 }}>
                  📅 {t.doneDate}
                </span>
              )}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 5,
              flexShrink: 0,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {!inArchive && (
              <button
                onClick={() => markDone(t)}
                style={{
                  background: "rgba(52,211,153,.1)",
                  border: "1px solid rgba(52,211,153,.25)",
                  color: C.green,
                  borderRadius: 7,
                  padding: "4px 12px",
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                ✓ 完成
              </button>
            )}
            {inArchive && (
              <button onClick={() => restore(t)} style={bSm}>
                ↩ 還原
              </button>
            )}
            <button onClick={() => openEdit(t)} style={bEdit}>
              編輯
            </button>
            <button
              onClick={() => {
                setTasks((l) => l.filter((x) => x.id !== t.id));
                showToast("已刪除");
              }}
              style={bDel}
            >
              刪除
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
        執行項目
      </div>
      <div style={{ fontSize: 13, color: C.text3, marginBottom: 16 }}>
        記錄 AI 專案外的額外執行工作，完成後自動封存
      </div>
      <div style={card}>
        <CTtl>
          📌 進行中項目
          <button
            onClick={openAdd}
            style={{ ...bA, marginLeft: "auto", fontSize: 12 }}
          >
            ＋ 新增
          </button>
        </CTtl>
        {!active.length ? (
          <Empty icon="⚙️" text="目前沒有進行中的執行項目" />
        ) : (
          active.map((t) => <TaskCard key={t.id} t={t} inArchive={false} />)
        )}
      </div>
      <div style={card}>
        <CTtl>
          🗄 封存區（已完成）
          <span style={{ fontSize: 12, color: C.text3, fontWeight: 400 }}>
            {archived.length} 筆
          </span>
          {archived.length > 0 && (
            <button
              onClick={() => setShowArchive((s) => !s)}
              style={{ ...bG, marginLeft: "auto", fontSize: 12 }}
            >
              {showArchive ? "收合 ▲" : "展開 ▼"}
            </button>
          )}
        </CTtl>
        {!archived.length ? (
          <Empty icon="🗄" text="尚無已完成的封存項目" />
        ) : showArchive ? (
          archived
            .sort((a, b) => (b.doneDate || "").localeCompare(a.doneDate || ""))
            .map((t) => <TaskCard key={t.id} t={t} inArchive={true} />)
        ) : (
          <div
            style={{
              fontSize: 13,
              color: C.text3,
              textAlign: "center",
              padding: "8px 0",
            }}
          >
            共 {archived.length} 筆已完成項目，點「展開」查看
          </div>
        )}
      </div>
      {modal && (
        <Modal
          title={editing ? "編輯執行項目" : "新增執行項目"}
          onClose={() => setModal(false)}
          onSave={save}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <Fld label="項目名稱">
              <input
                style={fi}
                value={f("name")}
                placeholder="例：文宣設計、廣告投放…"
                onChange={(e) => sf("name", e.target.value)}
              />
            </Fld>
            <Fld label="細項說明">
              <textarea
                style={{ ...fi, minHeight: 60 }}
                value={f("detail")}
                placeholder="說明執行內容…"
                onChange={(e) => sf("detail", e.target.value)}
              />
            </Fld>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <Fld label="執行人員">
                <input
                  style={fi}
                  value={f("person")}
                  onChange={(e) => sf("person", e.target.value)}
                />
              </Fld>
              <Fld label="費用（NT$）">
                <input
                  style={fi}
                  type="number"
                  value={f("fee")}
                  placeholder="例：2000"
                  onChange={(e) => sf("fee", e.target.value)}
                />
              </Fld>
            </div>
            <Fld label="狀態">
              <select
                style={fi}
                value={f("status")}
                onChange={(e) => sf("status", e.target.value)}
              >
                <option>進行中</option>
                <option>完成</option>
                <option>待確認</option>
              </select>
            </Fld>
            <Fld label="完成日期">
              <input
                style={fi}
                type="date"
                value={f("doneDate")}
                onChange={(e) => sf("doneDate", e.target.value)}
              />
            </Fld>
            <div style={{ fontSize: 12, color: C.text3 }}>
              💡
              狀態設為「完成」會自動歸入封存區；完成日期所屬月份的費用會自動加總到「獎金發放」的其他費用。
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── ⑤ 獎金發放 ───────────────────────────────────────────
// ══════════════════════════════════════════════════════════
function PageBonus({ user, proj, showToast }) {
  const isAdmin = user.role === "admin",
    isPartner = user.role === "partner";
  const [bonuses, setBonuses] = useFStore(proj, "bonuses", []);
  const [perfs] = useFStore(proj, "performances", []);
  const [tasks] = useFStore(proj, "tasks", []);
  const [filterMonth, setFilterMonth] = useState(toMonth());
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({});
  const f = (k) => form[k] ?? "";
  const sf = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const months = [...new Set(bonuses.map((b) => b.month).filter(Boolean))]
    .sort()
    .reverse();
  // 夥伴只能看自己的獎金，管理員看全部
  const filtered = bonuses.filter(
    (b) =>
      (!filterMonth || b.month === filterMonth) &&
      (isAdmin || b.person === user.name)
  );
  // 統計數字：夥伴只算自己的
  const aiTotal = filtered.reduce((s, b) => s + (parseInt(b.ai) || 0), 0);
  const perfTotal = perfs
    .filter(
      (p) =>
        (!filterMonth || p.date?.startsWith(filterMonth)) &&
        (isAdmin || p.person === user.name)
    )
    .reduce((s, p) => s + (parseInt(p.bonus) || 0), 0);
  const taskTotal = tasks
    .filter(
      (t) =>
        t.doneDate &&
        (!filterMonth || t.doneDate.startsWith(filterMonth)) &&
        (isAdmin || t.person === user.name)
    )
    .reduce((s, t) => s + (parseInt(t.fee) || 0), 0);
  function openAdd() {
    setEditing(null);
    setForm({ month: filterMonth || toMonth(), partnerOk: 0, companyOk: 0 });
    setModal(true);
  }
  function openEdit(b) {
    setEditing(b);
    setForm({ ...b });
    setModal(true);
  }
  function save() {
    if (editing) {
      setBonuses((l) =>
        l.map((x) => (x.id === editing.id ? { ...x, ...form } : x))
      );
      showToast("已更新");
    } else {
      setBonuses((l) => [...l, { id: uid(), ...form }]);
      showToast("發放記錄已新增");
    }
    setModal(false);
  }
  function toggle(id, who) {
    if (who === "company" && !isAdmin) {
      showToast("只有管理員可確認匯款");
      return;
    }
    setBonuses((l) =>
      l.map((b) =>
        b.id == id
          ? {
              ...b,
              [who === "partner" ? "partnerOk" : "companyOk"]:
                b[who === "partner" ? "partnerOk" : "companyOk"] ^ 1,
            }
          : b
      )
    );
  }
  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
        獎金發放
      </div>
      <div style={{ fontSize: 13, color: C.text3, marginBottom: 16 }}>
        每月結算 AI 專案費 ＋ 績效獎金 ＋ 其他費用
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 14,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <select
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          style={{
            ...fi,
            width: "auto",
            fontSize: 13,
            padding: "8px 10px",
            flex: 1,
          }}
        >
          <option value={toMonth()}>{toMonth()}（本月）</option>
          {months
            .filter((m) => m !== toMonth())
            .map((m) => (
              <option key={m}>{m}</option>
            ))}
          <option value="">全部月份</option>
        </select>
        {filterMonth &&
          filtered.every((b) => b.partnerOk && b.companyOk) &&
          filtered.length > 0 && (
            <Tag bg="rgba(52,211,153,.1)" color={C.green}>
              ✓ 已全數結清
            </Tag>
          )}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2,1fr)",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <StatCard
          val={"$" + aiTotal.toLocaleString()}
          label="AI 專案費"
          color={C.accent}
          bc="rgba(56,189,248,.3)"
        />
        <StatCard
          val={"$" + perfTotal.toLocaleString()}
          label="績效獎金"
          color={C.green}
          bc="rgba(52,211,153,.3)"
        />
        <StatCard
          val={"$" + taskTotal.toLocaleString()}
          label="執行項目"
          color={C.amber}
          bc="rgba(251,191,36,.3)"
        />
        <StatCard
          val={"$" + (aiTotal + perfTotal + taskTotal).toLocaleString()}
          label="發放總額"
          color={C.purple}
          bc="rgba(167,139,250,.3)"
        />
      </div>
      <div style={card}>
        <CTtl>
          💸 人員發放明細
          {isAdmin && (
            <button
              onClick={openAdd}
              style={{ ...bA, marginLeft: "auto", fontSize: 12 }}
            >
              ＋ 新增
            </button>
          )}
        </CTtl>
        {!filtered.length ? (
          <Empty icon="💸" text="此月份尚無發放記錄" />
        ) : (
          filtered.map((b) => {
            const mo = b.month || "";
            const pp2 = perfs.filter(
              (p) => p.person === b.person && (!mo || p.date?.startsWith(mo))
            );
            const pp = pp2.reduce((s, p) => s + (parseInt(p.bonus) || 0), 0);
            // 自動加總：該人員完成日期在該月份的執行項目費用
            const taskList = tasks.filter(
              (t) =>
                t.person === b.person &&
                t.doneDate &&
                (!mo || t.doneDate.startsWith(mo))
            );
            const taskSum = taskList.reduce(
              (s, t) => s + (parseInt(t.fee) || 0),
              0
            );
            const otherTotal = (parseInt(b.other) || 0) + taskSum;
            const tot = (parseInt(b.ai) || 0) + pp + otherTotal,
              done = b.partnerOk && b.companyOk,
              expanded = expandedId === b.id;
            return (
              <div
                key={b.id}
                style={{
                  background: C.bg3,
                  borderRadius: 8,
                  border:
                    "1px solid " + (done ? "rgba(52,211,153,.3)" : C.border),
                  padding: 14,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: C.adim,
                      border: "1px solid " + C.accent,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      color: C.accent,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {b.person?.[0] || "?"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {b.person}
                    </div>
                    <div style={{ fontSize: 11, color: C.text3 }}>
                      {b.month}
                    </div>
                  </div>
                  <div
                    style={{
                      fontFamily: "monospace",
                      fontSize: 18,
                      color: C.purple,
                      fontWeight: 700,
                    }}
                  >
                    NT$ {tot.toLocaleString()}
                  </div>
                </div>
                {isAdmin && (
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      marginBottom: 12,
                      justifyContent: "flex-end",
                    }}
                  >
                    <button onClick={() => openEdit(b)} style={bEdit}>
                      編輯
                    </button>
                    <button
                      onClick={() => {
                        setBonuses((l) => l.filter((x) => x.id != b.id));
                        showToast("已刪除");
                      }}
                      style={bDel}
                    >
                      刪除
                    </button>
                  </div>
                )}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,1fr)",
                    marginBottom: 12,
                    textAlign: "center",
                    background: C.bg2,
                    borderRadius: 8,
                    padding: "10px 0",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 11, color: C.text3 }}>
                      AI 專案費
                    </div>
                    <div style={{ color: C.accent, fontWeight: 600 }}>
                      ${(parseInt(b.ai) || 0).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.text3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 3,
                      }}
                    >
                      績效
                      {(pp2.length > 0 || taskList.length > 0) && (
                        <button
                          onClick={() => setExpandedId(expanded ? null : b.id)}
                          style={{ ...bSm, padding: "0 5px", fontSize: 10 }}
                        >
                          {expanded ? "▲" : "▼"}
                        </button>
                      )}
                    </div>
                    <div style={{ color: C.green, fontWeight: 600 }}>
                      ${pp.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: C.text3 }}>其他費用</div>
                    <div style={{ color: C.amber, fontWeight: 600 }}>
                      ${otherTotal.toLocaleString()}
                    </div>
                    {taskSum > 0 && (
                      <div style={{ fontSize: 9, color: C.text3 }}>
                        含項目 ${taskSum.toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
                {expanded && (pp2.length > 0 || taskList.length > 0) && (
                  <div
                    style={{
                      background: C.bg2,
                      borderRadius: 8,
                      padding: "10px 12px",
                      marginBottom: 12,
                    }}
                  >
                    {pp2.length > 0 && (
                      <div
                        style={{
                          fontSize: 11,
                          color: C.text3,
                          marginBottom: 6,
                        }}
                      >
                        績效明細
                      </div>
                    )}
                    {pp2.map((p) => (
                      <div
                        key={p.id}
                        style={{
                          display: "flex",
                          gap: 8,
                          padding: "5px 0",
                          borderBottom: "1px solid " + C.border,
                          fontSize: 12,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ color: C.text3, flexShrink: 0 }}>
                          {p.date}
                        </span>
                        <Tag bg="rgba(52,211,153,.1)" color={C.green}>
                          {p.kpi}
                        </Tag>
                        <span style={{ color: C.text3, flex: 1, fontSize: 11 }}>
                          {p.detail}
                        </span>
                        <span
                          style={{
                            color: C.green,
                            fontFamily: "monospace",
                            flexShrink: 0,
                          }}
                        >
                          {p.bonus ? "NT$ " + p.bonus : "—"}
                        </span>
                      </div>
                    ))}
                    {taskList.length > 0 && (
                      <div
                        style={{
                          fontSize: 11,
                          color: C.text3,
                          margin: "8px 0 6px",
                        }}
                      >
                        執行項目明細（自動帶入）
                      </div>
                    )}
                    {taskList.map((t) => (
                      <div
                        key={t.id}
                        style={{
                          display: "flex",
                          gap: 8,
                          padding: "5px 0",
                          borderBottom: "1px solid " + C.border,
                          fontSize: 12,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ color: C.text3, flexShrink: 0 }}>
                          {t.doneDate}
                        </span>
                        <Tag bg="rgba(251,191,36,.1)" color={C.amber}>
                          {t.name}
                        </Tag>
                        <span style={{ color: C.text3, flex: 1, fontSize: 11 }}>
                          {t.detail}
                        </span>
                        <span
                          style={{
                            color: C.amber,
                            fontFamily: "monospace",
                            flexShrink: 0,
                          }}
                        >
                          {t.fee ? "NT$ " + t.fee : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: 14,
                    alignItems: "center",
                    paddingTop: 11,
                    borderTop: "1px solid " + C.border,
                    flexWrap: "wrap",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      fontSize: 13,
                      color: C.text2,
                    }}
                  >
                    <CBox
                      checked={!!b.partnerOk}
                      onClick={() => toggle(b.id, "partner")}
                    />
                    <span>夥伴確認</span>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      fontSize: 13,
                      color: C.text2,
                    }}
                  >
                    <CBox
                      checked={!!b.companyOk}
                      onClick={() => toggle(b.id, "company")}
                    />
                    <span>公司匯款完成</span>
                  </label>
                  {done && (
                    <Tag bg="rgba(52,211,153,.1)" color={C.green}>
                      ✓ 本月結清
                    </Tag>
                  )}
                </div>
                {isPartner && (
                  <div style={{ marginTop: 10, fontSize: 12, color: C.amber }}>
                    🔒 獎金由管理員設定，您只能在確認後打勾。
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {modal && (
        <Modal
          title={editing ? "編輯發放記錄" : "新增發放記錄"}
          onClose={() => setModal(false)}
          onSave={save}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <Fld label="人員姓名">
              <input
                style={fi}
                value={f("person")}
                placeholder="外包夥伴姓名"
                onChange={(e) => sf("person", e.target.value)}
              />
            </Fld>
            <Fld label="結算月份">
              <input
                style={fi}
                type="month"
                value={f("month")}
                onChange={(e) => sf("month", e.target.value)}
              />
            </Fld>
            <Fld label="AI 專案費（NT$）">
              <input
                style={fi}
                type="number"
                value={f("ai")}
                placeholder="例：3000"
                onChange={(e) => sf("ai", e.target.value)}
              />
            </Fld>
            <Fld label="其他費用（NT$）">
              <input
                style={fi}
                type="number"
                value={f("other")}
                placeholder="例：500（選填）"
                onChange={(e) => sf("other", e.target.value)}
              />
            </Fld>
            <div style={{ fontSize: 12, color: C.text3 }}>
              💡
              績效獎金自動從「數據績效」帶入；執行項目費用依完成日期月份自動加入其他費用。此欄位為額外手動費用（選填）。
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── ⑥ 公司拆帳 ───────────────────────────────────────────
// ══════════════════════════════════════════════════════════
function PageSplit({ user, proj, showToast }) {
  const isAdmin = user.role === "admin";
  const [splits, setSplits] = useFStore(proj, "splits", []);
  const [hist, setHist] = useFStore(proj, "splits-hist", []);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const f = (k) => form[k] ?? "";
  const sf = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const received = splits.reduce(
    (s, r) => s + (r.ichengOk ? parseInt(r.amount) || 0 : 0),
    0
  );
  const pending = splits.reduce(
    (s, r) => s + (!r.ichengOk ? parseInt(r.amount) || 0 : 0),
    0
  );
  const allDone =
    splits.length > 0 && splits.every((r) => r.subOk && r.ichengOk);
  const anyDone = splits.some((r) => r.ichengOk);
  function openAdd() {
    setEditing(null);
    setForm({ company: COMPANIES[0], month: toMonth(), subOk: 0, ichengOk: 0 });
    setModal(true);
  }
  function openEdit(r) {
    setEditing(r);
    setForm({ ...r });
    setModal(true);
  }
  function save() {
    if (editing) {
      setSplits((l) =>
        l.map((x) => (x.id === editing.id ? { ...x, ...form } : x))
      );
      showToast("已更新");
    } else {
      setSplits((l) => [...l, { id: uid(), ...form }]);
      showToast("款項記錄已新增");
    }
    setModal(false);
  }
  function toggle(id, who) {
    if (who === "icheng" && !isAdmin) {
      showToast("只有管理員可確認到帳");
      return;
    }
    setSplits((l) => {
      const nl = l.map((r) =>
        r.id == id
          ? {
              ...r,
              [who === "sub" ? "subOk" : "ichengOk"]:
                r[who === "sub" ? "subOk" : "ichengOk"] ^ 1,
            }
          : r
      );
      const upd = nl.find((r) => r.id == id);
      if (upd?.subOk && upd?.ichengOk && !hist.find((h) => h.id == id))
        setHist((h) => [...h, { ...upd }]);
      return nl;
    });
  }
  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
        公司拆帳
      </div>
      <div style={{ fontSize: 13, color: C.text3, marginBottom: 16 }}>
        各子公司確認匯款至鈊誠數位，再統一發放給專案夥伴
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <StatCard
          val={"$" + received.toLocaleString()}
          label="已確認匯入"
          color={C.accent}
          bc="rgba(56,189,248,.3)"
        />
        <StatCard
          val={"$" + pending.toLocaleString()}
          label="待確認"
          color={C.rose}
          bc="rgba(251,113,133,.3)"
        />
        <StatCard
          val={allDone ? "全數到帳" : anyDone ? "部分到帳" : "等待中"}
          label="到帳狀態"
          color={allDone ? C.green : anyDone ? C.amber : C.rose}
          bc="rgba(52,211,153,.3)"
        />
      </div>
      <div style={card}>
        <CTtl>
          🏢 子公司匯款確認
          {isAdmin && (
            <button
              onClick={openAdd}
              style={{ ...bA, marginLeft: "auto", fontSize: 12 }}
            >
              ＋ 新增
            </button>
          )}
        </CTtl>
        <IBar type="blue">
          📋 流程：子公司確認匯出 → 鈊誠數位確認到帳 → 前往獎金發放執行匯款
        </IBar>
        {!splits.length ? (
          <Empty icon="🏢" text="尚未新增本月款項記錄" />
        ) : (
          splits.map((r) => {
            const a = parseInt(r.amount) || 0,
              done = r.subOk && r.ichengOk;
            return (
              <div
                key={r.id}
                style={{
                  background: C.bg3,
                  borderRadius: 8,
                  border:
                    "1px solid " + (done ? "rgba(52,211,153,.3)" : C.border),
                  padding: 14,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                      {r.company}
                    </span>
                    <span
                      style={{ fontSize: 12, color: C.text3, marginLeft: 8 }}
                    >
                      {r.month}
                    </span>
                  </div>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: 16,
                      color: C.accent,
                      fontWeight: 600,
                    }}
                  >
                    NT$ {a.toLocaleString()}
                  </span>
                  {isAdmin && (
                    <div style={{ display: "flex", gap: 5 }}>
                      <button onClick={() => openEdit(r)} style={bEdit}>
                        編輯
                      </button>
                      <button
                        onClick={() => {
                          setSplits((l) => l.filter((x) => x.id != r.id));
                          showToast("已刪除");
                        }}
                        style={bDel}
                      >
                        刪除
                      </button>
                    </div>
                  )}
                </div>
                {r.note && (
                  <div
                    style={{ fontSize: 12, color: C.text3, marginBottom: 10 }}
                  >
                    📝 {r.note}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: 14,
                    alignItems: "center",
                    paddingTop: 10,
                    borderTop: "1px solid " + C.border,
                    flexWrap: "wrap",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      fontSize: 13,
                      color: C.text2,
                    }}
                  >
                    <CBox
                      checked={!!r.subOk}
                      onClick={() => toggle(r.id, "sub")}
                    />
                    <span>{r.company} 確認匯出</span>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      fontSize: 13,
                      color: C.text2,
                    }}
                  >
                    <CBox
                      checked={!!r.ichengOk}
                      onClick={() => toggle(r.id, "icheng")}
                    />
                    <span>鈊誠數位確認到帳</span>
                  </label>
                  {done && (
                    <Tag bg="rgba(52,211,153,.1)" color={C.green}>
                      ✓ 款項完成
                    </Tag>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      <div style={card}>
        <CTtl>📜 歷史記錄</CTtl>
        {!hist.length ? (
          <Empty icon="📜" text="尚無歷史記錄" />
        ) : (
          hist.map((r) => (
            <div
              key={r.id}
              style={{
                background: C.bg3,
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 8,
                border: "1px solid " + C.border,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontSize: 12, color: C.text3 }}>{r.month}</span>
                <strong>{r.company}</strong>
                <span style={{ fontFamily: "monospace", color: C.accent }}>
                  NT$ {parseInt(r.amount).toLocaleString()}
                </span>
                <Tag
                  bg={r.subOk ? "rgba(52,211,153,.1)" : "rgba(251,113,133,.1)"}
                  color={r.subOk ? C.green : C.rose}
                >
                  {r.subOk ? "已確認" : "未確認"}
                </Tag>
                <Tag
                  bg={
                    r.ichengOk ? "rgba(52,211,153,.1)" : "rgba(251,113,133,.1)"
                  }
                  color={r.ichengOk ? C.green : C.rose}
                >
                  {r.ichengOk ? "已到帳" : "未到帳"}
                </Tag>
              </div>
              {r.note && (
                <div style={{ fontSize: 12, color: C.text3, marginTop: 6 }}>
                  📝 {r.note}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {modal && (
        <Modal
          title={editing ? "編輯款項" : "新增子公司款項"}
          onClose={() => setModal(false)}
          onSave={save}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <Fld label="子公司">
              <select
                style={fi}
                value={f("company")}
                onChange={(e) => sf("company", e.target.value)}
              >
                {COMPANIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </Fld>
            <Fld label="結算月份">
              <input
                style={fi}
                type="month"
                value={f("month")}
                onChange={(e) => sf("month", e.target.value)}
              />
            </Fld>
            <Fld label="應匯金額（NT$）">
              <input
                style={fi}
                type="number"
                value={f("amount")}
                placeholder="例：5000"
                onChange={(e) => sf("amount", e.target.value)}
              />
            </Fld>
            <Fld label="備註說明">
              <input
                style={fi}
                value={f("note")}
                placeholder="例：含A客戶2月份AI專案費分攤"
                onChange={(e) => sf("note", e.target.value)}
              />
            </Fld>
          </div>
        </Modal>
      )}
    </div>
  );
}
